import * as THREE from 'three'
import bodyVertex from '@/shaders/body.vert.glsl?raw'
import sunFragment from '@/shaders/sun.frag.glsl?raw'
import glowVertex from '@/shaders/glow.vert.glsl?raw'
import glowFragment from '@/shaders/glow.frag.glsl?raw'
import { withNoise } from '@/_utilities/shader'
import { damp } from '@/_utilities/math'
import type { CelestialBody } from '@/_types/celestial-body'

/**
 * The central QuickCasa sun: a churning shader-driven star wrapped in two additive
 * corona shells. It pulses gently and can flare on demand for easter eggs.
 */
export class Sun {
  public readonly object: THREE.Group
  public readonly pickMesh: THREE.Mesh
  public readonly body: CelestialBody

  private readonly surfaceMaterial: THREE.ShaderMaterial
  private readonly innerCorona: THREE.ShaderMaterial
  private readonly outerCorona: THREE.ShaderMaterial
  private readonly baseIntensity: number
  private flareTarget: number
  private flareCurrent: number

  /**
   * @param {CelestialBody} body - The sun's data entry from the constants file.
   */
  public constructor(body: CelestialBody) {
    this.body = body
    this.object = new THREE.Group()
    this.baseIntensity = 1.35
    this.flareTarget = 0
    this.flareCurrent = 0

    this.surfaceMaterial = new THREE.ShaderMaterial({
      vertexShader: bodyVertex,
      fragmentShader: withNoise(sunFragment),
      uniforms: {
        uTime: { value: 0 },
        uCoreColour: { value: new THREE.Color(body.glowColour) },
        uFlareColour: { value: new THREE.Color(body.accentColour) },
        uEdgeColour: { value: new THREE.Color(body.surfaceColour) },
        uNoiseScale: { value: body.noiseScale },
        uIntensity: { value: this.baseIntensity },
      },
    })

    this.pickMesh = new THREE.Mesh(new THREE.SphereGeometry(body.radius, 64, 64), this.surfaceMaterial)
    this.pickMesh.userData.bodyId = body.id
    this.object.add(this.pickMesh)

    this.innerCorona = this.createCoronaMaterial(body.glowColour, 1.6, 2.2)
    this.object.add(this.createCoronaShell(body.radius * 1.35, this.innerCorona))

    this.outerCorona = this.createCoronaMaterial(body.accentColour, 0.7, 3.4)
    this.object.add(this.createCoronaShell(body.radius * 2.1, this.outerCorona))
  }

  /**
   * Advance the sun's surface animation, rotation, and pulsing intensity.
   *
   * @param {number} elapsedSeconds - Total elapsed time since start.
   * @param {number} deltaSeconds - Time since the previous frame.
   * @returns {void}
   */
  public update(elapsedSeconds: number, deltaSeconds: number): void {
    this.flareCurrent = damp(this.flareCurrent, this.flareTarget, 0.06, deltaSeconds)
    this.flareTarget = damp(this.flareTarget, 0, 0.02, deltaSeconds)

    this.pickMesh.rotation.y += deltaSeconds * this.body.spinSpeed
    const pulse = 1 + Math.sin(elapsedSeconds * 1.4) * 0.06 + this.flareCurrent

    this.surfaceMaterial.uniforms.uTime.value = elapsedSeconds
    this.surfaceMaterial.uniforms.uIntensity.value = this.baseIntensity * pulse
    this.innerCorona.uniforms.uIntensity.value = 1.6 * pulse
    this.outerCorona.uniforms.uIntensity.value = 0.7 * pulse
  }

  /**
   * Trigger a brief, bright solar flare (used when the sun is clicked).
   *
   * @returns {void}
   */
  public flare(): void {
    this.flareTarget = 0.8
  }

  /**
   * Build an additive fresnel corona material.
   *
   * @param {string} colour - The corona tint.
   * @param {number} intensity - The base brightness of the halo.
   * @param {number} power - The fresnel falloff exponent (higher is tighter).
   * @returns {THREE.ShaderMaterial} The configured corona material.
   */
  private createCoronaMaterial(colour: string, intensity: number, power: number): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: glowVertex,
      fragmentShader: glowFragment,
      transparent: true,
      side: THREE.BackSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uColour: { value: new THREE.Color(colour) },
        uIntensity: { value: intensity },
        uPower: { value: power },
      },
    })
  }

  /**
   * Build a corona shell mesh at a given radius.
   *
   * @param {number} radius - The shell radius.
   * @param {THREE.ShaderMaterial} material - The corona material to apply.
   * @returns {THREE.Mesh} The corona shell mesh.
   */
  private createCoronaShell(radius: number, material: THREE.ShaderMaterial): THREE.Mesh {
    const shell = new THREE.Mesh(new THREE.SphereGeometry(radius, 48, 48), material)
    return shell
  }
}
